<template>
  <div class="notification-bell">
    <el-badge :value="unreadCount" :hidden="unreadCount === 0" class="notification-badge">
      <el-button
        type="text"
        @click="showNotifications"
        class="bell-button"
      >
        <span class="bell-emoji">🔔</span>
      </el-button>
    </el-badge>

    <!-- 通知弹窗 -->
    <el-dialog
      title="通知中心"
      :visible.sync="notificationDialogVisible"
      width="600px"
      :modal="true"
      :close-on-click-modal="true"
      :show-close="true"
      append-to-body
        >
      <div class="notification-content">
        <div v-if="notifications.length === 0" class="no-notifications">
          <i class="el-icon-bell"></i>
          <p>暂无通知</p>
        </div>
        
        <div v-else class="notification-list">
          <div
            v-for="notification in notifications"
            :key="notification.id"
            class="notification-item"
            :class="{ 'unread': !notification.is_read }"
            @click="markAsRead(notification)"
          >
            <div class="notification-header">
              <h4>{{ notification.title }}</h4>
              <div class="notification-meta">
                <span class="notification-time">{{ formatDate(notification.created_at) }}</span>
                <el-tag v-if="!notification.is_read" type="danger" size="mini">未读</el-tag>
              </div>
            </div>
            <div class="notification-content-text">
              <p>{{ notification.content }}</p>
            </div>
            <div v-if="notification.invitation_id" class="notification-actions">
              <el-button
                type="text"
                size="small"
                @click.stop="viewRelatedInvitation(notification)"
              >
                查看详情
              </el-button>
            </div>
          </div>
        </div>
      </div>

      <!-- 分页 -->
      <div v-if="notifications.length > 0" class="notification-pagination">
        <el-pagination
          @current-change="handlePageChange"
          :current-page="pagination.page"
          :page-size="pagination.page_size"
          layout="prev, pager, next"
          :total="pagination.total"
          small
        />
      </div>

      <div slot="footer" class="dialog-footer">
        <el-button
          type="danger"
          @click="deleteAllNotifications"
          :disabled="notifications.length === 0"
        >
          全部删除
        </el-button>
        <el-button @click="markAllAsRead" :disabled="unreadCount === 0">
          全部标记为已读
        </el-button>
        <el-button type="primary" @click="notificationDialogVisible = false">
          关闭
        </el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import request from '@/utils/request'
import config from '@/config'

export default {
  name: 'NotificationBell',
  data() {
    return {
      notificationDialogVisible: false,
      notifications: [],
      unreadCount: 0,
      pagination: {
        page: 1,
        page_size: 10,
        total: 0
      },
      loading: false,
      eventSource: null,
      retryCount: 0  // SSE重试次数
    }
  },
  created() {
    // 检查是否已登录，只有登录后才建立SSE连接
    const token = localStorage.getItem('token')
    if (token) {
      // 先获取一次初始值
      this.getUnreadCount()
      // 然后建立SSE连接
      this.connectSSE()
    }
  },
  beforeDestroy() {
    // 关闭SSE连接
    if (this.eventSource) {
      this.eventSource.close()
      this.eventSource = null
      console.log('🔔 [NotificationBell] SSE连接已关闭')
    }
  },
  methods: {
    connectSSE() {
      // 先关闭已存在的连接
      if (this.eventSource) {
        this.eventSource.close()
        this.eventSource = null
      }

      const token = localStorage.getItem('token')
      if (!token) {
        this.unreadCount = 0
        this.retryCount = 0
        return
      }

      // 获取API基础URL
      const baseURL = config.baseURL || '/api'
      // 构建完整的SSE URL
      let sseUrl = `${baseURL}/expert-invitations/notifications/stream`
      
      // 如果baseURL是相对路径，需要转换为绝对路径
      if (baseURL.startsWith('/')) {
        const protocol = window.location.protocol
        const host = window.location.host
        sseUrl = `${protocol}//${host}${baseURL}/expert-invitations/notifications/stream`
      }

      console.log('🔔 [NotificationBell] 开始建立SSE连接:', sseUrl)

      // 创建EventSource连接
      // 注意：EventSource不支持自定义headers，所以需要在URL中传递token
      this.eventSource = new EventSource(`${sseUrl}?token=${encodeURIComponent(token)}`)

      // 监听消息
      this.eventSource.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data)
          if (data.error) {
            console.error('🔔 [NotificationBell] SSE错误:', data.error)
            return
          }
          if (data.unread_count !== undefined) {
            this.unreadCount = data.unread_count
            console.log('🔔 [NotificationBell] SSE推送未读数量:', this.unreadCount)
          }
        } catch (error) {
          console.error('🔔 [NotificationBell] 解析SSE消息失败:', error)
        }
      }

      // 监听连接打开
      this.eventSource.onopen = () => {
        console.log('🔔 [NotificationBell] SSE连接已建立')
        // 连接成功，重置重试计数
        this.retryCount = 0
      }

      // 监听错误
      this.eventSource.onerror = (error) => {
        console.error('🔔 [NotificationBell] SSE连接错误:', error)
        // 检查连接状态
        if (this.eventSource.readyState === EventSource.CLOSED) {
          // 检查是否是401错误（token无效）
          const token = localStorage.getItem('token')
          if (!token) {
            console.log('🔔 [NotificationBell] 无token，停止SSE连接')
            this.unreadCount = 0
            return
          }
          
          // 如果是401错误，说明token可能已过期，不要频繁重试
          // 等待更长时间再重试，或者等待用户重新登录
          console.log('🔔 [NotificationBell] SSE连接已关闭，可能是token过期，30秒后尝试重连...')
          setTimeout(() => {
            const currentToken = localStorage.getItem('token')
            if (currentToken && currentToken === token) {
              // token没变，说明用户没有重新登录，可能是token过期
              // 只重试一次，如果还是失败就不再重试
              if (!this.retryCount) {
                this.retryCount = 1
                this.connectSSE()
              } else {
                console.log('🔔 [NotificationBell] SSE重连失败，停止重试（可能需要重新登录）')
                this.unreadCount = 0
              }
            } else {
              // token变了，说明用户重新登录了，可以重试
              this.retryCount = 0
              this.connectSSE()
            }
          }, 30000) // 30秒后重试
        }
      }
    },

    async getUnreadCount() {
      // 保留此方法用于初始加载或手动刷新
      // SSE会自动更新，但首次加载时可能需要立即获取
      const token = localStorage.getItem('token')
      if (!token) {
        this.unreadCount = 0
        return
      }
      
      try {
        console.log('🔔 [NotificationBell] 手动获取未读通知数量...')
        const response = await request.get('/notifications/unread-count')
        this.unreadCount = response?.unread_count || 0
        console.log('🔔 [NotificationBell] 未读数量设置为:', this.unreadCount)
      } catch (error) {
        // 如果是401错误，说明token已过期，清除未读数量并关闭SSE
        if (error.response && error.response.status === 401) {
          console.log('🔔 [NotificationBell] 401未授权，清除未读数量并关闭SSE')
          this.unreadCount = 0
          if (this.eventSource) {
            this.eventSource.close()
            this.eventSource = null
          }
          return
        }
        console.error('🔔 [NotificationBell] 获取未读通知数量失败:', error)
      }
    },

    async showNotifications() {
      this.notificationDialogVisible = true
      await this.getNotifications()
    },

    async getNotifications() {
      try {
        this.loading = true
        const params = {
          page: this.pagination.page,
          page_size: this.pagination.page_size
        }

        console.log('🔔 [NotificationBell] 开始获取通知列表...', params)
        const response = await request.get('/notifications', { params })
        console.log('🔔 [NotificationBell] 通知列表API响应:', response)
        
        this.notifications = response.items || []
        this.pagination.total = response.total || 0
        this.unreadCount = response.unread_count || 0
        
        console.log('🔔 [NotificationBell] 通知列表:', this.notifications)
        console.log('🔔 [NotificationBell] 未读数量:', this.unreadCount)
      } catch (error) {
        console.error('🔔 [NotificationBell] 获取通知列表失败:', error)
        console.error('🔔 [NotificationBell] 错误详情:', {
          message: error.message,
          response: error.response,
          status: error.response?.status,
          data: error.response?.data
        })
        this.$message.error('获取通知列表失败')
      } finally {
        this.loading = false
      }
    },

    async markAsRead(notification) {
      if (notification.is_read) return

      try {
        await request.put(`/notifications/${notification.id}/read`)
        notification.is_read = true
        this.unreadCount = Math.max(0, this.unreadCount - 1)
      } catch (error) {
        console.error('标记通知已读失败:', error)
        this.$message.error('标记通知已读失败')
      }
    },

    async markAllAsRead() {
      try {
        // 标记所有未读通知为已读
        const unreadNotifications = this.notifications.filter(n => !n.is_read)
        const promises = unreadNotifications.map(notification => 
          request.put(`/notifications/${notification.id}/read`)
        )
        
        await Promise.all(promises)
        
        // 更新本地状态
        this.notifications.forEach(notification => {
          notification.is_read = true
        })
        this.unreadCount = 0
        
        this.$message.success('所有通知已标记为已读')
      } catch (error) {
        console.error('标记所有通知已读失败:', error)
        this.$message.error('标记所有通知已读失败')
      }
    },

    async deleteAllNotifications() {
      if (!this.notifications.length) return

      try {
        await this.$confirm('确定要删除全部通知吗？该操作无法撤销。', '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        })
      } catch {
        return
      }

      try {
        await request.delete('/notifications')
        this.notifications = []
        this.unreadCount = 0
        this.pagination.total = 0
        this.pagination.page = 1
        this.$message.success('通知已全部删除')
      } catch (error) {
        console.error('删除全部通知失败:', error)
        this.$message.error('删除全部通知失败')
      }
    },

    viewRelatedInvitation(notification) {
      // 根据用户类型跳转到相应页面
      const user = this.$store.getters['auth/user']
      
      let targetPath = ''
      if (user && user.user_type === 'enterprise') {
        // 企业用户跳转到用户中心的邀请专家页面
        targetPath = '/user-center?tab=invite-expert'
      } else if (user && user.user_type === 'expert') {
        // 专家用户跳转到用户中心的专家回复页面
        targetPath = '/user-center?tab=expert-reply'
      }
      
      // 避免重复导航
      if (targetPath && this.$route.fullPath !== targetPath) {
        this.$router.push(targetPath).catch(err => {
          // 忽略导航重复错误
          if (err.name !== 'NavigationDuplicated') {
            console.error('导航失败:', err)
          }
        })
      }
      
      this.notificationDialogVisible = false
    },

    handlePageChange(page) {
      this.pagination.page = page
      this.getNotifications()
    },

    formatDate(dateStr) {
      if (!dateStr) return ''
      const date = new Date(dateStr)
      const now = new Date()
      const diff = now - date
      
      // 小于1分钟
      if (diff < 60000) {
        return '刚刚'
      }
      // 小于1小时
      if (diff < 3600000) {
        return Math.floor(diff / 60000) + '分钟前'
      }
      // 小于1天
      if (diff < 86400000) {
        return Math.floor(diff / 3600000) + '小时前'
      }
      // 小于7天
      if (diff < 604800000) {
        return Math.floor(diff / 86400000) + '天前'
      }
      
      return date.toLocaleDateString('zh-CN')
    }
  }
}
</script>

<style scoped>
.notification-bell {
  position: relative;
}

.notification-badge {
  margin-right: 10px;
}

.bell-button {
  font-size: 18px;
  color: #606266;
  padding: 8px;
}

.bell-button:hover {
  color: #409eff;
}

.bell-emoji {
  font-size: 20px;
  display: inline-block;
}

.notification-content {
  max-height: 400px;
  overflow-y: auto;
}

.no-notifications {
  text-align: center;
  padding: 40px 20px;
  color: #909399;
}

.no-notifications i {
  font-size: 48px;
  margin-bottom: 16px;
  display: block;
}

.notification-list {
  max-height: 400px;
  overflow-y: auto;
}

.notification-item {
  padding: 15px;
  border-bottom: 1px solid #ebeef5;
  cursor: pointer;
  transition: background-color 0.3s;
}

.notification-item:hover {
  background-color: #f5f7fa;
}

.notification-item.unread {
  background-color: #f0f9ff;
  border-left: 3px solid #409eff;
}

.notification-item:last-child {
  border-bottom: none;
}

.notification-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 8px;
}

.notification-header h4 {
  margin: 0;
  font-size: 14px;
  color: #303133;
  font-weight: 500;
}

.notification-meta {
  display: flex;
  align-items: center;
  gap: 8px;
}

.notification-time {
  font-size: 12px;
  color: #909399;
}

.notification-content-text {
  margin-bottom: 8px;
}

.notification-content-text p {
  margin: 0;
  font-size: 13px;
  color: #606266;
  line-height: 1.5;
}

.notification-actions {
  text-align: right;
}

.notification-pagination {
  margin-top: 15px;
  text-align: center;
}

.dialog-footer {
  text-align: right;
}
</style>
